import * as React from "react";
import {
  FieldValues,
  Path,
  RegisterOptions,
  useFormContext,
} from "react-hook-form";
import { useLanguage } from "./Language";
import { BaseTextFieldProps, TextField } from "@mui/material";

// TODO add @param debounce (optional) in ms to provide debounce for callbacks
/**
 * 
 * @param name 
 * @param callback be sure to memoize callback param
 */
export const useWatchWithCallback = <T extends FieldValues, K extends Path<T>>(
  name: K,
  callback: (value: T[K]) => void,
) => {
  const { watch, getValues } = useFormContext<T>();
  React.useEffect(() => {
    const subscription = watch((value, { name: fieldName, type }) => {
      if (name === fieldName) {
        if (process.env.NODE_ENV !== "production") {
          console.log("FormTextField watch", name, type, getValues(name));
        }
        callback(getValues(name));
      } else if (!!!fieldName && !!!type) {
        if (process.env.NODE_ENV !== "production") {
          console.log("FormTextField reset", name, "reset", getValues(name));
        }
        callback(getValues(name));
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, getValues, name, callback]);
};

export interface FormTextFieldProps<T extends FieldValues>
  extends Pick<BaseTextFieldProps, "type" | "helperText" | "disabled" | "sx"> {
  name: Path<T>;
  label: string;
  required?: boolean;
  maxLength?: number;
  options?: Partial<RegisterOptions<T>>;
  preserveWhitespace?: boolean;
}

export const FormTextField = <T extends FieldValues>({
  name,
  label,
  required,
  disabled,
  maxLength,
  sx,
  type,
  options,
  helperText,
  preserveWhitespace,
}: FormTextFieldProps<T>) => {
  const { translate } = useLanguage();
  const { getFieldState, register, getValues } = useFormContext<T>();
  const { error } = getFieldState(name);

  // Leaving these notes about watch here for future component dev:
  // const currentValue = watch(name); // this is very bad, it triggers a top level re-render of the form
  // this is a little better, but will still trigger a re-render of only this component each time the user types
  // const currentValue = useWatch({name: name});
  // this is much better, it doesn't trigger a re-render, and we can use the callback function to set filled, 
  // instead of creating a state variable for frequently changing textfield input
  // useWatchWithCallback(name, (value) => setFilled(!!value));

  // this is best, it handles initial value and reset while onBlur handles the rest
  const currentValue = getValues(name);
  const [filled, setFilled] = React.useState<boolean>(!!currentValue);
  React.useEffect(() => {
    setFilled(!!currentValue); // use effect is necessary to update the state on subsequent re-render from reset
  }, [currentValue]);

  const [focused, setFocused] = React.useState<boolean>(false);

  // Note use deps register option instead of watch for cross field validation triggers
  const registerOptions: RegisterOptions<T> = React.useMemo(
    () => ({
      setValueAs: (value) => {
        if (
          preserveWhitespace === true ||
          !value ||
          typeof value !== "string"
        ) {
          return value;
        }
        return value.trim();
      },
      required: required && `${translate("isRequired", {
        LABEL: label
      })}`,
      maxLength: !!maxLength && {
        value: maxLength,
        message: `${translate(
          "maxLengthError",
          {
            LABEL: label,
            MAX_LENGTH: maxLength.toString(),
          }
        )}`,
      },
      ...options,
    }),
    [label, maxLength, preserveWhitespace, required, translate, options]
  );

  // I considered useController instead of the register API (uncontrolled)
  // it better isolates the input from the rest of the form state
  // I could have kept the TextField uncontrolled by simply omitting value from the field props that are spread
  // uncontrolled is ideal for a large form filled with many text inputs.
  // Ultimately, the lack of deps or setValueAs in the registerOptions for useController was enough
  // to stick with the register API
  const { ref, onBlur, ...field } = register(name, registerOptions);

  if (process.env.NODE_ENV !== "production") {
    console.log("FormTextField render", name, currentValue);
  }

  // useMemo or useCallback on any objects passed as props to TextField that could trigger re-renders in child components
  // this is not necessary for primitives, and probably over kill on components that won't be reused a ton
  const inputLabelProps = React.useMemo(
    () => ({
      shrink: filled || focused,
    }),
    [filled, focused]
  );

  const sxProp = React.useMemo(
    () => ({ maxWidth: { sm: "320px" }, ...sx }),
    [sx]
  );

  const handleOnBlur = React.useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      onBlur(event);
      setFilled(!!event.target.value)
      setFocused(false);
    },
    [onBlur]
  );

  const handleOnFocus = React.useCallback(() => {
    setFocused(true);
  }, []);

  return (
    <TextField
      sx={sxProp}
      label={label}
      disabled={disabled}
      // upgrade MUI to v6+ then change to
      // slotProps={{
      //   inputLabel: {
      //     shrink: filled || focused
      //   }
      // }}
      InputLabelProps={inputLabelProps}
      required={required || !!options?.required}
      inputRef={ref}
      onBlur={handleOnBlur}
      onFocus={handleOnFocus}
      {...field}
      error={!!error}
      helperText={error?.message || helperText || " "}
      type={type}
    />
  );
};
